import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarRange,
  CheckCircle2,
  ChevronRight,
  FileCog,
  Layers,
  Lock,
  PencilLine,
  Plus,
  Search,
  Users,
} from "lucide-react";
import { Modal } from "../components/Modal";
import { EmptyState, ErrorState, LoadingState } from "../components/PageState";
import {
  appraisalService,
  type AppraisalTemplate,
  type AssignableStaff,
  type TemplateVersion,
} from "../services/appraisalService";
import TemplateVersionEditor from "../components/appraisal/TemplateVersionEditor";
import { academicSetupService } from "../services/academicSetupService";
import { usePermissions } from "../hooks/usePermissions";
import { apiErrorMessage } from "../utils/apiError";

type Tab = "templates" | "cycles";

/**
 * Which version of a template a new cycle should run against.
 *
 * Pure and exported: picking the wrong version here would bind a whole round
 * of appraisals to the wrong form, which cannot be corrected afterwards
 * without discarding them.
 */
export function runnableVersion(template: AppraisalTemplate): TemplateVersion | null {
  const published = template.versions.filter((v) => v.is_published);
  if (!published.length) return null;
  return published.reduce((latest, v) =>
    v.version_number > latest.version_number ? v : latest,
  );
}

/** Why a template cannot be used yet, or null when it can. */
export function blockedReason(template: AppraisalTemplate): string | null {
  if (!template.is_active) return "This template is inactive.";
  if (!runnableVersion(template)) {
    return "No published version yet — publish one before running a cycle.";
  }
  return null;
}

/** A short, readable description of which stages a form puts people through. */
export function describeStages(version: TemplateVersion): string {
  const stages = ["Supervisor assessment"];
  if (version.has_self_assessment) stages.unshift("Self-assessment");
  if (version.has_review_meeting) stages.push("Review meeting");
  if (version.has_development_planning) stages.push("Development planning");
  stages.push("Approval", "Acknowledgement");
  return stages.join(" → ");
}

/** Whether a template can be used, is mid-revision, or is not usable yet.
 *
 * Pure and exported because it drives both the status dot in the list and the
 * counts above it: a template with no published version cannot run a cycle at
 * all, which is a different problem from one that simply has a draft open.
 */
export type TemplateHealth = "ready" | "revising" | "unpublished";

export function templateHealth(template: AppraisalTemplate): TemplateHealth {
  if (!runnableVersion(template)) return "unpublished";
  return template.versions.some((v) => !v.is_published) ? "revising" : "ready";
}

const HEALTH_META: Record<
  TemplateHealth,
  { dot: string; label: string; chip: string }
> = {
  ready: {
    dot: "bg-emerald-500",
    label: "Ready to run",
    chip: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  },
  revising: {
    dot: "bg-gold-500",
    label: "Draft open",
    chip: "bg-gold-50 text-gold-700 ring-gold-200",
  },
  unpublished: {
    dot: "bg-secondary-400",
    label: "Not published",
    chip: "bg-secondary-100 text-secondary-700 ring-secondary-300",
  },
};

/** A figure and what it counts. Values stay proportional-figure, per the
 * stat-tile contract -- tabular numerals are for columns, not headline numbers. */
function StatTile({
  label,
  value,
  icon: Icon,
  accent,
  active,
  onClick,
}: {
  label: string;
  value: number;
  icon: typeof Layers;
  accent: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-xl border px-4 py-3 text-left transition-colors ${
        active
          ? "border-navy-600 bg-navy-50"
          : "border-secondary-200 bg-white hover:border-navy-300"
      }`}
    >
      <span className="flex items-center gap-2">
        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${accent}`}>
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <span className="text-xs leading-tight text-secondary-600">{label}</span>
      </span>
      <span className="mt-2 block text-2xl font-semibold leading-none text-secondary-900">
        {value}
      </span>
    </button>
  );
}

function NewTemplateDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (template: AppraisalTemplate) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      appraisalService.createTemplate({ name: name.trim(), staff_category: category.trim() }),
    onSuccess: (template) => {
      setName("");
      setCategory("");
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["appraisal-templates"] });
      onCreated(template);
      onClose();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "That template could not be created.")),
  });

  return (
    <Modal isOpen={open} title="New appraisal template" onClose={onClose} size="lg">
      <div className="space-y-4">
        <p className="text-sm leading-relaxed text-secondary-600">
          A template holds the form. Each published version is frozen, so appraisals
          already answered against it never change when you revise it.
        </p>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-secondary-700">Name</span>
          <input
            className="input w-full"
            placeholder="e.g. Support Staff Appraisal"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-secondary-700">
            Who it is for
          </span>
          <input
            className="input w-full"
            placeholder="e.g. Support staff"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
          <span className="mt-1 block text-xs text-secondary-500">
            Descriptive only. It does not restrict who can be assigned this form.
          </span>
        </label>
        {error && (
          <p className="rounded-lg border border-maroon-200 bg-maroon-50 px-3 py-2 text-sm text-maroon-800">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 border-t border-secondary-100 pt-4">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn bg-navy-700 text-white hover:bg-navy-800"
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Creating…" : "Create template"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function VersionRow({
  version,
  inUse,
  selected,
  canManage,
  publishing,
  onSelect,
  onPublish,
}: {
  version: TemplateVersion;
  inUse: boolean;
  selected: boolean;
  canManage: boolean;
  publishing: boolean;
  onSelect: () => void;
  onPublish: () => void;
}) {
  const empty = version.question_count === 0;
  return (
    <li>
      <div
        className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-3 py-2.5 transition-colors ${
          selected
            ? "border-navy-600 bg-navy-50/70"
            : "border-secondary-200 bg-white hover:border-navy-300"
        }`}
      >
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          aria-pressed={selected}
          onClick={onSelect}
        >
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${
              version.is_published
                ? "bg-emerald-100 text-emerald-800"
                : "bg-gold-100 text-gold-700"
            }`}
          >
            v{version.version_number}
          </span>

          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-1.5">
              {version.is_published ? (
                <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-800 ring-1 ring-emerald-200">
                  <Lock className="h-3 w-3" aria-hidden />
                  Published
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded bg-gold-50 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gold-700 ring-1 ring-gold-200">
                  <PencilLine className="h-3 w-3" aria-hidden />
                  Draft
                </span>
              )}
              {inUse && (
                <span className="rounded bg-navy-50 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-navy-700 ring-1 ring-navy-200">
                  In use
                </span>
              )}
              <span className="text-xs text-secondary-600">
                {version.question_count} question
                {version.question_count === 1 ? "" : "s"}
                {version.appraisal_count > 0 && (
                  <> · {version.appraisal_count} answered</>
                )}
              </span>
            </span>
            <span
              className="mt-1 block truncate text-[11px] text-secondary-500"
              title={describeStages(version)}
            >
              {describeStages(version)}
            </span>
          </span>
        </button>

        <span className="flex shrink-0 items-center gap-2">
          {!version.is_published && canManage && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={publishing || empty}
              title={
                empty
                  ? "Add at least one question before publishing."
                  : "Freeze this version so cycles can run against it."
              }
              onClick={onPublish}
            >
              Publish
            </button>
          )}
          <button
            type="button"
            className={`inline-flex min-h-9 items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
              selected
                ? "bg-navy-700 text-white"
                : "text-navy-700 hover:bg-navy-50"
            }`}
            onClick={onSelect}
          >
            {selected ? "Open" : version.is_published || !canManage ? "View" : "Edit"}
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          </button>
        </span>
      </div>
    </li>
  );
}

function TemplatesTab() {
  const queryClient = useQueryClient();
  const { hasCapability } = usePermissions();
  // The page is also reachable with only the cycles capability, so the form
  // builder has to say plainly that it is read-only rather than offering
  // buttons the server will refuse.
  const canManage = hasCapability("access.appraisal.manage_templates");

  const [search, setSearch] = useState("");
  const [healthFilter, setHealthFilter] = useState<TemplateHealth | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ["appraisal-templates"],
    queryFn: appraisalService.getTemplates,
  });

  const newVersion = useMutation({
    mutationFn: (id: number) => appraisalService.newVersion(id),
    onSuccess: (version) => {
      setError(null);
      // Open the new draft straight away -- a version with nothing in it is
      // the one thing a person always has more to do to.
      setSelectedTemplate(version.template);
      setSelectedVersion(version.id);
      void queryClient.invalidateQueries({ queryKey: ["appraisal-templates"] });
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "A new version could not be started.")),
  });

  const publish = useMutation({
    mutationFn: (versionId: number) => appraisalService.publishVersion(versionId),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["appraisal-templates"] });
    },
    onError: (err) =>
      setError(
        apiErrorMessage(
          err,
          "That version could not be published. It needs at least one question.",
        ),
      ),
  });

  const all = templates.data ?? [];
  const counts = {
    total: all.length,
    ready: all.filter((t) => templateHealth(t) === "ready").length,
    revising: all.filter((t) => templateHealth(t) === "revising").length,
    unpublished: all.filter((t) => templateHealth(t) === "unpublished").length,
  };

  const needle = search.trim().toLowerCase();
  const visible = all.filter((template) => {
    if (healthFilter && templateHealth(template) !== healthFilter) return false;
    if (!needle) return true;
    return (
      template.name.toLowerCase().includes(needle) ||
      (template.staff_category ?? "").toLowerCase().includes(needle)
    );
  });

  // Follow the list rather than holding a selection the filter has hidden.
  const active =
    visible.find((t) => t.id === selectedTemplate) ?? visible[0] ?? null;
  const versions = active
    ? [...active.versions].sort((a, b) => b.version_number - a.version_number)
    : [];
  const runnable = active ? runnableVersion(active) : null;
  // Default to whatever needs attention: the open draft, else the live form.
  const openVersion =
    versions.find((v) => v.id === selectedVersion) ??
    versions.find((v) => !v.is_published) ??
    versions[0] ??
    null;

  return (
    <div className="space-y-4">
      {error && (
        <p
          className="flex items-start gap-2 rounded-lg border border-maroon-200 bg-maroon-50 px-3 py-2 text-sm text-maroon-800"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {error}
        </p>
      )}

      {!canManage && (
        <p className="flex items-start gap-2 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-secondary-600">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          You can see the forms but not change them. Creating templates, editing
          questions and publishing a version need the “Appraisal: manage templates”
          capability, which an administrator grants on the Permissions page.
        </p>
      )}

      {/* The four numbers worth knowing, each one also a filter. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Templates"
          value={counts.total}
          icon={Layers}
          accent="bg-navy-50 text-navy-600"
          active={healthFilter === null}
          onClick={() => setHealthFilter(null)}
        />
        <StatTile
          label="Ready to run"
          value={counts.ready}
          icon={CheckCircle2}
          accent="bg-emerald-50 text-emerald-600"
          active={healthFilter === "ready"}
          onClick={() => setHealthFilter(healthFilter === "ready" ? null : "ready")}
        />
        <StatTile
          label="Draft open"
          value={counts.revising}
          icon={PencilLine}
          accent="bg-gold-50 text-gold-600"
          active={healthFilter === "revising"}
          onClick={() =>
            setHealthFilter(healthFilter === "revising" ? null : "revising")
          }
        />
        <StatTile
          label="Not published"
          value={counts.unpublished}
          icon={AlertTriangle}
          accent="bg-secondary-100 text-secondary-600"
          active={healthFilter === "unpublished"}
          onClick={() =>
            setHealthFilter(healthFilter === "unpublished" ? null : "unpublished")
          }
        />
      </div>

      {templates.isLoading && <LoadingState label="Loading templates…" />}
      {templates.isError && (
        <ErrorState
          message="Templates could not be loaded."
          retry={() => void templates.refetch()}
        />
      )}

      {!templates.isLoading && all.length === 0 && (
        <EmptyState
          title="No templates yet"
          message="Create one to start. Different staff categories can each have their own form."
          icon={FileCog}
        />
      )}

      {all.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-[19rem_1fr] lg:items-start">
          {/* Finder */}
          <aside className="card overflow-hidden">
            <div className="border-b border-secondary-100 p-3">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400"
                  aria-hidden
                />
                <input
                  type="search"
                  className="input w-full pl-9"
                  placeholder="Find a template…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Find a template"
                />
              </div>
            </div>

            <ul className="max-h-[28rem] divide-y divide-secondary-100 overflow-y-auto scrollbar-hover">
              {visible.length === 0 && (
                <li className="px-4 py-6 text-center text-sm text-secondary-500">
                  Nothing matches that.
                </li>
              )}
              {visible.map((template) => {
                const health = templateHealth(template);
                const meta = HEALTH_META[health];
                const isActive = active?.id === template.id;
                return (
                  <li key={template.id}>
                    <button
                      type="button"
                      aria-current={isActive}
                      className={`flex w-full items-start gap-2.5 px-3.5 py-3 text-left transition-colors ${
                        isActive
                          ? "bg-navy-50 shadow-[inset_3px_0_0_0_theme(colors.navy.700)]"
                          : "hover:bg-secondary-50"
                      }`}
                      onClick={() => {
                        setSelectedTemplate(template.id);
                        setSelectedVersion(null);
                      }}
                    >
                      <span
                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${meta.dot}`}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-secondary-900">
                          {template.name}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-secondary-500">
                          {template.staff_category || "Any staff"} ·{" "}
                          {template.versions.length} version
                          {template.versions.length === 1 ? "" : "s"}
                        </span>
                        <span className="sr-only">{meta.label}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {canManage && (
              <div className="border-t border-secondary-100 p-3">
                <button
                  type="button"
                  className="btn btn-outline w-full border-dashed"
                  onClick={() => setCreating(true)}
                >
                  <Plus className="mr-1 h-4 w-4" aria-hidden />
                  New template
                </button>
              </div>
            )}
          </aside>

          {/* The selected template */}
          {active && (
            <section className="card overflow-hidden">
              <header className="border-b border-secondary-100 bg-secondary-50/60 px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-secondary-900">
                      {active.name}
                    </h2>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-secondary-500">
                      <span>{active.staff_category || "Any staff"}</span>
                      <span aria-hidden>·</span>
                      <span
                        className={`rounded-full px-2 py-0.5 font-semibold ring-1 ${
                          HEALTH_META[templateHealth(active)].chip
                        }`}
                      >
                        {HEALTH_META[templateHealth(active)].label}
                      </span>
                      {active.created_by_name && (
                        <>
                          <span aria-hidden>·</span>
                          <span>created by {active.created_by_name}</span>
                        </>
                      )}
                    </p>
                  </div>
                  {canManage && (
                    <button
                      type="button"
                      className="btn bg-navy-700 text-white hover:bg-navy-800"
                      disabled={newVersion.isPending}
                      title={
                        versions.length
                          ? "Start a draft copy of the latest version to revise."
                          : "Start the first version of this form."
                      }
                      onClick={() => newVersion.mutate(active.id)}
                    >
                      <Plus className="mr-1 h-4 w-4" aria-hidden />
                      {newVersion.isPending
                        ? "Creating…"
                        : versions.length
                          ? "New version"
                          : "Start first version"}
                    </button>
                  )}
                </div>

                {blockedReason(active) && (
                  <p className="mt-3 flex items-start gap-2 rounded-lg border border-gold-200 bg-gold-50 px-3 py-2 text-xs text-gold-700">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                    {blockedReason(active)}
                  </p>
                )}
              </header>

              <div className="space-y-4 p-5">
                {versions.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-secondary-300 px-4 py-8 text-center text-sm text-secondary-500">
                    No versions yet. Use{" "}
                    <strong className="font-semibold">Start first version</strong> to
                    create a draft, add your sections and questions, then publish it.
                  </p>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                        Versions · newest first
                      </h3>
                      <span className="text-xs text-secondary-400">
                        Published versions are frozen
                      </span>
                    </div>

                    <ul className="space-y-2">
                      {versions.map((version) => (
                        <VersionRow
                          key={version.id}
                          version={version}
                          inUse={runnable?.id === version.id}
                          selected={openVersion?.id === version.id}
                          canManage={canManage}
                          publishing={publish.isPending}
                          onSelect={() => setSelectedVersion(version.id)}
                          onPublish={() => publish.mutate(version.id)}
                        />
                      ))}
                    </ul>

                    {openVersion && (
                      <div className="rounded-xl border border-secondary-200 bg-secondary-50/40 p-4">
                        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-secondary-500">
                          v{openVersion.version_number} · sections and questions
                        </h3>
                        <TemplateVersionEditor
                          version={openVersion}
                          canEdit={canManage}
                          onRevise={() => newVersion.mutate(active.id)}
                          revising={newVersion.isPending}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
          )}
        </div>
      )}

      <NewTemplateDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(template) => {
          setSelectedTemplate(template.id);
          setSelectedVersion(null);
          setHealthFilter(null);
          setSearch("");
        }}
      />
    </div>
  );
}

function CyclesTab() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: "",
    template_version: "",
    academic_year: "",
    period_start: "",
    period_end: "",
    self_assessment_due: "",
    supervisor_review_due: "",
    final_approval_due: "",
  });
  const [selected, setSelected] = useState<number[]>([]);
  // Per-person supervisor overrides, keyed by staff profile id. Only holds the
  // ones actually chosen here; anyone left out falls back to the reporting
  // line, then their department head.
  const [supervisors, setSupervisors] = useState<Record<number, number>>({});
  const [activeCycle, setActiveCycle] = useState<number | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ["appraisal-templates"],
    queryFn: appraisalService.getTemplates,
  });
  const cycles = useQuery({
    queryKey: ["appraisal-cycles"],
    queryFn: appraisalService.getCycles,
  });
  const years = useQuery({
    queryKey: ["academic-years"],
    queryFn: academicSetupService.getYears,
  });
  const staff = useQuery({
    queryKey: ["appraisal-assignable", activeCycle],
    queryFn: () => appraisalService.getAssignableStaff(activeCycle ?? undefined),
    enabled: activeCycle !== null,
  });
  const supervisorOptions = useQuery({
    queryKey: ["appraisal-eligible-supervisors"],
    queryFn: appraisalService.getEligibleSupervisors,
    enabled: activeCycle !== null,
  });

  const publishedVersions = (templates.data ?? [])
    .flatMap((t) => t.versions.filter((v) => v.is_published))
    .sort((a, b) => a.template_name.localeCompare(b.template_name));

  const create = useMutation({
    mutationFn: () =>
      appraisalService.createCycle({
        name: form.name,
        template_version: Number(form.template_version),
        academic_year: form.academic_year ? Number(form.academic_year) : null,
        period_start: form.period_start,
        period_end: form.period_end,
        self_assessment_due: form.self_assessment_due || null,
        supervisor_review_due: form.supervisor_review_due || null,
        final_approval_due: form.final_approval_due || null,
        status: "open",
      }),
    onSuccess: () => {
      setError(null);
      setForm({ ...form, name: "" });
      void queryClient.invalidateQueries({ queryKey: ["appraisal-cycles"] });
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: Record<string, unknown> } })?.response
        ?.data;
      setError(
        data ? Object.values(data).flat().join(" ") : "That cycle could not be created.",
      );
    },
  });

  const assign = useMutation({
    mutationFn: () => appraisalService.assign(activeCycle!, selected, supervisors),
    onSuccess: (data) => {
      setSelected([]);
      setSupervisors({});
      setResult(
        `Assigned ${data.created_count}.` +
          (data.skipped.length
            ? ` Skipped ${data.skipped.length}: ${data.skipped
                .map((s) => s.reason)
                .join("; ")}`
            : ""),
      );
      void queryClient.invalidateQueries({ queryKey: ["appraisal-cycles"] });
      void queryClient.invalidateQueries({ queryKey: ["appraisal-assignable"] });
      void queryClient.invalidateQueries({ queryKey: ["appraisal-dashboard"] });
    },
    onError: () => setError("Those appraisals could not be assigned."),
  });

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="card-content space-y-3">
          <h2 className="font-semibold">New appraisal cycle</h2>
          <p className="text-sm text-gray-500">
            A cycle is one round — who is appraised, over what period, and by when.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <input
              className="input"
              placeholder="e.g. 2026 Teacher Annual Appraisal"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <select
              className="input"
              value={form.template_version}
              onChange={(e) => setForm({ ...form, template_version: e.target.value })}
            >
              <option value="">Choose a published form…</option>
              {publishedVersions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.template_name} v{v.version_number}
                </option>
              ))}
            </select>
            {/* Optional by design -- a probation review runs on its own dates
                and period_start/period_end stay the authority on what is being
                assessed. Offered because without it nothing in the app could
                ever set the link, so every cycle came out unattached. */}
            <label className="text-sm text-gray-600">
              Academic year
              <select
                className="input w-full"
                value={form.academic_year}
                onChange={(e) => setForm({ ...form, academic_year: e.target.value })}
              >
                <option value="">Not tied to a year</option>
                {(years.data ?? []).map((year) => (
                  <option key={year.id} value={year.id}>
                    {year.name}
                    {year.is_current ? " (current)" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-600">
              Period start
              <input
                type="date"
                className="input w-full"
                value={form.period_start}
                onChange={(e) => setForm({ ...form, period_start: e.target.value })}
              />
            </label>
            <label className="text-sm text-gray-600">
              Period end
              <input
                type="date"
                className="input w-full"
                value={form.period_end}
                onChange={(e) => setForm({ ...form, period_end: e.target.value })}
              />
            </label>
            <label className="text-sm text-gray-600">
              Self-assessment due
              <input
                type="date"
                className="input w-full"
                value={form.self_assessment_due}
                onChange={(e) =>
                  setForm({ ...form, self_assessment_due: e.target.value })
                }
              />
            </label>
            <label className="text-sm text-gray-600">
              Supervisor review due
              <input
                type="date"
                className="input w-full"
                value={form.supervisor_review_due}
                onChange={(e) =>
                  setForm({ ...form, supervisor_review_due: e.target.value })
                }
              />
            </label>
            <label className="text-sm text-gray-600">
              Final approval due
              <input
                type="date"
                className="input w-full"
                value={form.final_approval_due}
                onChange={(e) =>
                  setForm({ ...form, final_approval_due: e.target.value })
                }
              />
            </label>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              !form.name.trim() ||
              !form.template_version ||
              !form.period_start ||
              !form.period_end ||
              create.isPending
            }
            onClick={() => create.mutate()}
          >
            <CalendarRange className="mr-1 h-4 w-4" aria-hidden />
            Create cycle
          </button>
          {error && <p className="text-sm text-red-700">{error}</p>}
        </div>
      </div>

      {cycles.isLoading && <LoadingState label="Loading cycles…" />}
      {(cycles.data ?? []).map((cycle) => (
        <div key={cycle.id} className="card">
          <div className="card-content space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold">{cycle.name}</h3>
                <p className="text-sm text-gray-500">
                  {cycle.template_name} v{cycle.version_number} · {cycle.period_start} to{" "}
                  {cycle.period_end} · {cycle.appraisal_count} assigned
                  {/* Says which year a round belongs to, and says plainly when
                      it belongs to none, rather than leaving the reader to
                      wonder whether the link is missing or simply absent. */}
                  {" · "}
                  {cycle.academic_year
                    ? (years.data ?? []).find((y) => y.id === cycle.academic_year)?.name ??
                      "linked to a year"
                    : "no academic year"}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() =>
                  setActiveCycle(activeCycle === cycle.id ? null : cycle.id)
                }
              >
                <Users className="mr-1 h-4 w-4" aria-hidden />
                {activeCycle === cycle.id ? "Close" : "Assign staff"}
              </button>
            </div>

            {activeCycle === cycle.id && (
              <div className="space-y-2 border-t border-gray-100 pt-3">
                <p className="text-xs text-gray-500">
                  Each person is appraised by their reporting manager, or failing
                  that their department head. Where neither is recorded, or where
                  someone else should do it, choose a supervisor here. Only staff
                  permitted to conduct appraisals are listed.
                </p>
                {staff.isLoading && <LoadingState label="Loading staff…" />}
                {staff.data?.length === 0 && (
                  <p className="text-sm text-gray-500">
                    Everyone eligible is already in this cycle.
                  </p>
                )}
                {(staff.data ?? []).map((person: AssignableStaff) => (
                  <label
                    key={person.id}
                    className="flex items-center justify-between gap-3 rounded border border-gray-200 px-2 py-1.5 text-sm"
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selected.includes(person.id)}
                        onChange={(e) =>
                          setSelected((current) =>
                            e.target.checked
                              ? [...current, person.id]
                              : current.filter((id) => id !== person.id),
                          )
                        }
                      />
                      <span>
                        <span className="font-medium text-gray-900">
                          {person.full_name}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">
                          {person.department || "No department"} · {person.user_type}
                        </span>
                      </span>
                    </span>
                    <span className="flex items-center gap-2 text-xs text-gray-500">
                      <label className="sr-only" htmlFor={`sup-${person.id}`}>
                        Supervisor for {person.full_name}
                      </label>
                      <select
                        id={`sup-${person.id}`}
                        className="input py-1 text-xs"
                        value={
                          supervisors[person.id] ?? person.default_supervisor ?? ""
                        }
                        onChange={(e) =>
                          setSupervisors((current) => {
                            const next = { ...current };
                            if (e.target.value === "") {
                              delete next[person.id];
                            } else {
                              next[person.id] = Number(e.target.value);
                            }
                            return next;
                          })
                        }
                      >
                        <option value="">
                          {person.default_supervisor_name
                            ? `Default: ${person.default_supervisor_name}`
                            : "Choose a supervisor…"}
                        </option>
                        {(supervisorOptions.data ?? [])
                          // Nobody appraises themselves, so the person being
                          // assigned is not offered as their own supervisor.
                          .filter((candidate) => candidate.id !== person.user_id)
                          .map((candidate) => (
                            <option key={candidate.id} value={candidate.id}>
                              {candidate.full_name}
                            </option>
                          ))}
                      </select>
                      {!person.default_supervisor && !supervisors[person.id] && (
                        <span className="text-amber-700">
                          pick one, or this person is skipped
                        </span>
                      )}
                    </span>
                  </label>
                ))}
                {selected.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={assign.isPending}
                    onClick={() => assign.mutate()}
                  >
                    Assign {selected.length} appraisal
                    {selected.length === 1 ? "" : "s"}
                  </button>
                )}
                {result && <p className="text-sm text-gray-700">{result}</p>}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AppraisalTemplates() {
  const [tab, setTab] = useState<Tab>(() =>
    new URLSearchParams(window.location.search).get("tab") === "cycles"
      ? "cycles"
      : "templates",
  );

  const TABS: { value: Tab; label: string; hint: string; icon: typeof FileCog }[] = [
    {
      value: "templates",
      label: "Forms",
      hint: "Questions and versions",
      icon: FileCog,
    },
    {
      value: "cycles",
      label: "Rounds",
      hint: "Cycles and assignment",
      icon: CalendarRange,
    },
  ];

  return (
    <div className="space-y-5">
      <header className="card overflow-hidden">
        <div className="bg-gradient-to-br from-navy-800 to-navy-950 px-5 py-5 sm:px-6">
          <h1 className="text-2xl font-bold tracking-tight text-white">
            Appraisal setup
          </h1>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-navy-200">
            Forms and the rounds that run them. Publishing a form freezes it, so
            revising it creates a new version and completed appraisals are never
            rewritten.
          </p>
        </div>

        {/* Named for what they hold rather than for the data model: somebody
            looking for "the questions" should not have to know that a form is
            a template and a round is a cycle. */}
        <nav className="flex flex-wrap gap-1 border-t border-secondary-100 p-2">
          {TABS.map(({ value, label, hint, icon: Icon }) => (
            <button
              key={value}
              type="button"
              aria-current={tab === value}
              className={`flex flex-1 items-center gap-2.5 rounded-lg px-3.5 py-2.5 text-left transition-colors sm:flex-none ${
                tab === value
                  ? "bg-navy-700 text-white"
                  : "text-secondary-600 hover:bg-secondary-50"
              }`}
              onClick={() => setTab(value)}
            >
              <Icon
                className={`h-4 w-4 shrink-0 ${
                  tab === value ? "text-white" : "text-secondary-400"
                }`}
                aria-hidden
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold leading-tight">
                  {label}
                </span>
                <span
                  className={`block truncate text-[11px] leading-tight ${
                    tab === value ? "text-navy-200" : "text-secondary-400"
                  }`}
                >
                  {hint}
                </span>
              </span>
            </button>
          ))}
        </nav>
      </header>

      {tab === "templates" ? <TemplatesTab /> : <CyclesTab />}
    </div>
  );
}
